/**
 * InfoTooltip Component
 * Reusable info button with tooltip/modal for financial term explanations
 */

import React, { useState, useRef, useEffect } from 'react';
import { getTerm, GlossaryTerm } from '../../data/financialGlossary';

interface InfoTooltipProps {
  termKey?: string;           // Key to look up in glossary
  term?: GlossaryTerm;        // Or provide term directly
  title?: string;             // Custom title (overrides term)
  content?: string;           // Custom content (overrides term)
  size?: 'small' | 'medium';  // Icon size
  position?: 'top' | 'bottom' | 'left' | 'right';  // Tooltip position
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({
  termKey,
  term: providedTerm,
  title,
  content,
  size = 'small',
  position = 'top',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Get term from glossary if termKey provided
  const glossaryTerm = termKey ? getTerm(termKey) : providedTerm;

  // Use custom or glossary content
  const displayTitle = title || glossaryTerm?.term || 'Information';
  const displayContent = content || glossaryTerm?.fullDefinition || '';
  const taxLine = glossaryTerm?.taxLine;
  const examples = glossaryTerm?.examples;

  // Position tooltip relative to button
  useEffect(() => {
    if (isOpen && buttonRef.current && tooltipRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let style: React.CSSProperties = {};

      // Calculate position based on preference and available space
      switch (position) {
        case 'top':
          style = {
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '8px',
          };
          // If not enough space on top, flip to bottom
          if (buttonRect.top < tooltipRect.height + 20) {
            style = {
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '8px',
            };
          }
          break;
        case 'bottom':
          style = {
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '8px',
          };
          break;
        case 'left':
          style = {
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginRight: '8px',
          };
          break;
        case 'right':
          style = {
            left: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '8px',
          };
          break;
      }

      // Adjust horizontal position if tooltip would overflow viewport
      if (tooltipRef.current) {
        const tooltipLeft = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2;
        if (tooltipLeft < 10) {
          style.left = '0';
          style.transform = position === 'top' || position === 'bottom' ? 'none' : style.transform;
        } else if (tooltipLeft + tooltipRect.width > viewportWidth - 10) {
          style.left = 'auto';
          style.right = '0';
          style.transform = position === 'top' || position === 'bottom' ? 'none' : style.transform;
        }
      }

      setTooltipStyle(style);
    }
  }, [isOpen, position]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const iconSize = size === 'small' ? '16px' : '20px';

  return (
    <span className="info-tooltip-wrapper" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        ref={buttonRef}
        type="button"
        className="info-tooltip-btn"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        aria-label={`Information about ${displayTitle}`}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '2px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize,
          height: iconSize,
          borderRadius: '50%',
          color: '#6b7280',
          fontSize: size === 'small' ? '12px' : '14px',
          marginLeft: '4px',
          transition: 'color 0.2s, background 0.2s',
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        <svg
          width={size === 'small' ? 14 : 18}
          height={size === 'small' ? 14 : 18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>

      {isOpen && displayContent && (
        <div
          ref={tooltipRef}
          className="info-tooltip-content"
          role="tooltip"
          style={{
            position: 'absolute',
            zIndex: 1000,
            width: '280px',
            maxWidth: '90vw',
            padding: '12px 16px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '0.85rem',
            lineHeight: '1.5',
            color: '#374151',
            ...tooltipStyle,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#1e3a5f' }}>
            {displayTitle}
          </div>
          <div style={{ marginBottom: taxLine || examples ? '8px' : 0 }}>
            {displayContent}
          </div>
          {taxLine && (
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb'
            }}>
              CRA: {taxLine}
            </div>
          )}
          {examples && examples.length > 0 && (
            <div style={{
              fontSize: '0.75rem',
              color: '#059669',
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb',
            }}>
              <strong>Example:</strong> {examples[0]}
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default InfoTooltip;
